import * as z from "zod";
import { toolEnvelope } from "./schemas/envelope";
import type { ToolContextType } from "./schemas/scoping";

/**
 * The tools endpoint routes `POST :name` alongside `GET manifest`. Requiring a verb prefix is
 * what makes a tool name that shadows a sibling route unconstructable.
 */
export const TOOL_NAME_PATTERN = /^(get|list|derive|ask|run)_[a-z0-9_]+$/;

export type ToolVerb = "get" | "list" | "derive" | "ask" | "run";

/** The shape every tool name must take. The catalog's actual names are `ToolName`. */
export type ToolNameFormat = `${ToolVerb}_${string}`;

export type ToolDefinition<
	TInput extends z.ZodObject = z.ZodObject,
	TData extends z.ZodType = z.ZodType,
	TName extends ToolNameFormat = ToolNameFormat,
> = {
	name: TName;
	description: string;
	label: string;
	contexts: readonly ToolContextType[];
	implementation: "api" | "python";
	audience: "model" | "proxy";
	input: TInput;
	data: TData;
	output: ReturnType<typeof toolEnvelope<TData>>;
};

export const defineTool = <
	TInput extends z.ZodObject,
	TData extends z.ZodType,
	TName extends ToolNameFormat,
>(
	definition: Omit<ToolDefinition<TInput, TData, TName>, "output">,
): ToolDefinition<TInput, TData, TName> => ({
	...definition,
	output: toolEnvelope(definition.data),
});
